import _ from "lodash";
import update from "immutability-helper";

/**
 *
 * @param {string} cls
 * @param {object} obj map of conditions
 * @returns {string}
 */
export function classnames(cls, obj) {
  let classnames = [];
  if (typeof cls === "string") classnames.push(cls);
  else obj = cls;
  for (let i in obj) {
    if (obj[i]) classnames.push(i);
  }
  return classnames.join(" ");
}

/**
 * splices to index on arrays
 * sets key on objects
 *
 * @param {object} tree
 * @param {object} item
 * @param {string} path
 * @param {string|number} id
 */
export function addChild(tree = {}, item, path) {
  const isArr = isArray(path);
  // target, computed, path
  const cpath = [
    isArr ? removeLastIndex(path) : path,
    isArr ? "$splice" : "$set",
  ].join(".");
  const value = isArr ? [[getLastIndex(path), 0, item]] : item;
  const updatePath = _.set({}, cpath, value);
  try {
    return update(tree, updatePath);
  } catch (e) {
    console.log(e);
    return tree;
  }
}

/**
 * return path without last index
 *
 * @param {string} path
 * @param {string} op - optional operation to append to path
 */
export function removeLastIndex(path, op) {
  path = _.initial(path.split("."));
  if (op) path = path.concat(op);
  return path.join(".");
}

function isNumeric(str) {
  if (typeof str !== "string") return false; // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

/**
 * check if path ends with an array element
 *
 * abc.xyz => false
 * abc.children.0 => true
 *
 * @param {string} path
 * @returns {boolean}
 */
export function isArray(path) {
  path = path.split(".");
  return isNumeric(_.last(path));
}

/**
 * updatePath modifier
 *
 * @param {string} path
 * @param {object} obj
 */
function removeFromPath(path, pathObj = {}) {
  // abc.children.0 -> abc.children.$op
  const isArr = isArray(path);
  const targetPath = removeLastIndex(path, isArr ? "$splice" : "$unset");
  path = path.split(".");
  let value = [_.last(path)];
  if (isArr) value = [value.concat(1)];
  return _.set(pathObj, targetPath, value);
}

/**
 *
 * @param {object} tree
 * @param {string} path
 */
export function removeChild(tree, path) {
  const updatePath = removeFromPath(path);
  return update(tree, updatePath);
}

/**
 *
 * @param {string} path
 * @returns {number} integer index
 */
export function getLastIndex(path) {
  if (typeof path === "string") path = path.split(".");
  return parseInt(_.last(path), 10);
}

/**
 * update tree with a MOVE operation
 * move anything anywhere in the tree
 *
 * @param {object} tree - virtual dom
 * @param {string} from - from address in dom
 * @param {string} to - to address in dom
 * @returns {object} updated dom tree
 */
export function moveChild(tree, from, to) {
  const item = _.get(tree, from);
  let updatePath = {};
  let updatedTree = {};
  const fromIsArray = isArray(from);
  const toIsArray = isArray(to);
  if (fromIsArray) {
    const fromIdx = getLastIndex(from);
    const fromPath = removeLastIndex(from);
    const toPath = removeLastIndex(to);
    _.set(updatePath, fromPath + ".$splice", [[fromIdx, 1]]);
    const sKey = fromPath.split(".").length;
    // intersection
    // adjacent idx of fromIdx
    const toIdx = parseInt(to.split(".")[from.split(".").length - 1], 10);
    // adjust array index if anything was removed
    if (to.startsWith(fromPath) && fromPath !== toPath && fromIdx < toIdx) {
      to = to.split(".");
      to[sKey] = parseInt(to[sKey], 10) - 1;
      to = to.join(".");
    }
  } else {
    removeFromPath(from, updatePath);
  }
  updatedTree = update(tree, updatePath);
  updatePath = {};
  if (toIsArray) {
    const toIdx = getLastIndex(to);
    _.set(updatePath, removeLastIndex(to, "$splice"), [[toIdx, 0, item]]);
  } else {
    _.set(updatePath, to + ".$set", item);
  }
  return update(updatedTree, updatePath);
}

export function splitValueUnit(value = "", unit = "") {
  const matching = value
    .toString()
    .match(/^([+-]?(?:\d+|\d*\.\d+))([a-z]*|%)$/);
  if (matching) {
    if (matching[2]) unit = matching[2];
    value = matching[1];
    value = Number.isInteger(value) ? parseInt(value, 10) : parseFloat(value);
    return { value, unit };
  }
  return { value };
}

export function getMatchedCSSRules(el, css = el.ownerDocument.styleSheets) {
  const stylesheets = {};
  const ss = [...css];
  for (let i in ss) {
    /** @type {StyleSheet} */
    const s = ss[i];
    try {
      const cssRules = [...s.cssRules];
      for (let i in cssRules) {
        /** @type {CSSStyleRule} */
        const r = cssRules[i];
        const selector = r.selectorText;
        if (el.matches(selector)) {
          const title = s.title;
          // prevent adding default styles
          if (!title) break;
          if (!stylesheets[title]) stylesheets[title] = {};
          const stylesheet = stylesheets[title];
          /** @type {CSS2Properties} */
          const css = r.style;
          stylesheet[selector] = css;
        }
      }
    } catch (e) {}
  }
  return stylesheets;
}

export const updateComponent = (tree, path, data) => {
  const value =
    typeof data === "object" ? { ..._.get(tree, path, {}), ...data } : data;
  const updatePath = {};
  const sPath = path.split(".");
  // console.log(sPath)
  for (let i = 1; i <= sPath.length; i++) {
    const cPath = sPath.slice(0, i).join(".");
    const pathEmpty = _.get(tree, cPath) === undefined;
    // console.log(i, _.get(tree, cPath))
    if (pathEmpty) {
      // console.log('setting non existing path', cPath);
      _.set(updatePath, cPath + ".$set", {});
    }
  }
  _.set(updatePath, path + ".$set", value);
  try {
    // console.log(updatePath)
    const updatedTree = update(tree, updatePath);
    // console.log(updatedTree)
    return updatedTree;
  } catch (e) {
    return tree;
  }
};

export const fetchAsText = async (s) => {
  const rsp = await fetch(s);
  const text = await rsp.text();
  return text;
};

export const fetchStylesheet = (link) =>
  fetchAsText(link).then((css) => {
    const title = _.last(link.split("/"));
    return { css, title };
  });

export function onReturnKey(callback) {
  return ({ keyCode, target }) => {
    if (keyCode === 13) callback(target.value);
  };
}

export function onDeleteKey(callback) {
  return ({ keyCode, target }) => {
    if (keyCode === 46) callback(target.value);
  };
}

export function onEscKey(callback) {
  return ({ keyCode, target }) => {
    if (keyCode === 27) callback(target.value);
  };
}

export function onTabKey(callback) {
  return ({ keyCode, target }) => {
    if (keyCode === 9) callback(target.value);
  };
}

/**
 * simple compounding function
 * used by css attributes input
 *
 * @param {string} str
 * @param {string} delim
 * @returns {string}
 */
export function splitLast(str, delim) {
  return _.last(str.split(delim));
}

/**
 * safely add item to an array
 *
 * @param {object} tree
 * @param {string} key
 * @param {any} item
 * @returns {object} tree
 */
export function safeAddToTree(tree, key, item) {
  return {
    ...tree,
    [key]: [..._.get(tree, key, []), item],
  };
}

export function getConfigMap(schema) {
  const configMap = {};
  for (let category of schema) {
    const config = category.config;
    const components = category.components;
    if (config) {
      for (let component of components) {
        configMap[component.type] = component.config || config;
      }
    }
  }
  return configMap;
}

export function getSetupMap(schema) {
  const setupMap = {
    // LIST: () => console.log('setup')
  };
  for (let category of schema) {
    const setup = category.setup;
    const components = category.components;
    if (setup) {
      for (let component of components) {
        setupMap[component.type] = setup;
      }
    }
  }
  return setupMap;
}

export function setupTree(schema, tree) {
  const setupMap = getSetupMap(schema);
  const setup = (c) => {
    if (c.type in setupMap) setupMap[c.type](c);
    if (c.type === "LIST") {
      c.children.forEach((c) => setup(c));
    }
    return c;
  };
  return {
    ...tree,
    body: _.mapValues(tree.body, setup),
  };
}

export function getNamepath(tree, path) {
  const pathTokens = path.split(".");
  const newPath = [];
  const getComponentAt = (i) =>
    _.get(tree.body, pathTokens.slice(0, i).join("."));
  for (let i = 0; i < pathTokens.length; i++) {
    let component = getComponentAt(i + 1);
    const id = _.get(component, "attribute.id");
    if (id) {
      newPath.push(id);
    } else if (!Array.isArray(component)) {
      let nPath = pathTokens[i];
      const parent = getComponentAt(i - 1);
      if (parent && parent.type === "LIST") nPath = "children." + nPath;
      newPath.push(nPath);
    }
  }
  return newPath.length ? newPath.join(".") : path;
}
